'use client'

import { toDateOnStartDay } from '../../../../../MyStichtingOpenHelpers/src/static'
import { ClearIcon, ScheduleIcon } from '../../icons'
import { enUS } from 'date-fns/locale/en-US'
import { nl } from 'date-fns/locale/nl'
import React from 'react'
import DatePicker, { registerLocale } from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { useInputWrapper } from '../../../hooks/useInputWrapper'

registerLocale('nl', nl)
registerLocale('en', enUS)

const dateFormatSeparators = ['/', '-', ' ', '.']

export interface FormDatePickerProps {
    isClearable?: boolean
    onChange: (option: Date | null) => void
    disabled?: boolean
    defaultDate?: Date | null
    placeholderText?: string
    /** @default 'off' */
    autoComplete?: string | undefined
    error?: boolean
    readOnly?: boolean
    minDate?: Date | null
    maxDate?: Date | null
    locale?: number
    id: string | undefined
    autoFocus?: boolean
    name?: string
    isMonthPicker?: boolean
    /**
     * The date format for this input using slashes
     * @default 'dd/MM/yyyy'
     */
    dateFormat?: string
    required?: boolean
}

export const FormDatePicker = ({
    onChange,
    isClearable = true,
    disabled = false,
    defaultDate = null,
    placeholderText = '',
    autoComplete = 'off',
    error,
    readOnly,
    minDate,
    maxDate,
    locale,
    id,
    autoFocus = false,
    name,
    isMonthPicker = false,
    dateFormat = 'dd/MM/yyyy',
    required = false,
    ...props
}: FormDatePickerProps) => {
    const { onFocusHandler, onBlurHandler, classes } = useInputWrapper(false, disabled)

    const getRegisteredLocale = (): string => {
        switch (locale) {
            case 0:
                return 'nl'
            case 1:
                return 'en'
            default:
                return 'nl'
        }
    }

    const changeHandler = (date: Date | null): void => {
        onChange(date ? toDateOnStartDay(date) : null)
        onBlurHandler()
    }

    /**
     * Creates a date format (based on the dateFormat prop) for each of the separators defined in dateFormatSeparators.
     * @returns array of date formats
     */
    const dateFormats = (): string[] => dateFormatSeparators.map((format) => dateFormat.replaceAll('/', format))

    return (
        <div {...props} className="datepicker-container" data-cy="input-datepicker">
            <DatePicker
                id={id}
                className={`datepicker ${error ? 'error' : ''} ${readOnly ? 'read-only' : ''} ${classes}`}
                selected={defaultDate}
                disabled={disabled}
                placeholderText={readOnly ? '' : placeholderText}
                readOnly={readOnly}
                onFocus={() => onFocusHandler()}
                onBlur={() => onBlurHandler()}
                onChange={changeHandler}
                minDate={minDate ?? undefined}
                maxDate={maxDate ?? undefined}
                dateFormatCalendar="MMM"
                showYearDropdown
                scrollableYearDropdown
                dateFormat={dateFormats()}
                locale={getRegisteredLocale()}
                yearDropdownItemNumber={30}
                autoFocus={autoFocus}
                autoComplete={autoComplete}
                name={name}
                showMonthYearPicker={isMonthPicker}
                popperProps={{ strategy: 'fixed' }}
                required={required}
            />

            {isClearable && !readOnly && defaultDate !== null && (
                <div className="absolute right-12 top-0 bottom-0 cursor-pointer w-10 text-accent-700 border-transparent hover:bg-accent-opacity-100 active:bg-accent-opacity-100 active:shadow-inner-md focus:bg-accent-opacity-100 focus:border-accent-600 inline-flex items-center justify-center">
                    <ClearIcon onClick={() => changeHandler(null)} />
                </div>
            )}

            {!readOnly && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-accent-700 inline-flex items-center justify-center">
                    <ScheduleIcon />
                </div>
            )}
        </div>
    )
}
